import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useSession } from '@/app/session';
import { useBackToClose } from '@/hooks/useBackToClose';
import { PasswordInput } from './PasswordInput';
import './session.css';

/** The words for a failure, whatever shape it arrived in. */
function messageOf(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message ? cause.message : fallback;
}

/**
 * The two session states that are *not* a sign-out but still need saying.
 *
 * - **`503 upstream_failure`.** The backend answers this when its datastore is
 *   unavailable, specifically so an outage cannot be mistaken for an expired
 *   session. So this is a banner, not a prompt and not a logout: the user keeps
 *   their session, keeps their cached mail, and is told it is being retried.
 * - **Imminent expiry.** `expiresAt` is known, so an idle user gets the offer
 *   to stay signed in *before* the session dies under a long compose, rather
 *   than a modal afterwards.
 * - **An unbound session** (ADR 0042). The session works and is protected by
 *   cookies alone. That reduced protection is *always* displayed: there is no
 *   state in which an unbound session is allowed to pass for a bound one, and
 *   the banner is the visible half of that rule. It is persistent by design,
 *   not dismissible — a dismissed banner is a silent fallback with extra steps.
 */
export function SessionBanner() {
  const { serviceDegraded, expiresSoon, expiresInMs, refresh, binding } = useSession();
  const [busy, setBusy] = useState(false);
  /**
   * Why "Stay signed in" did not work. It used to swallow the failure: the
   * button re-enabled, the countdown carried on, and the user had every reason
   * to believe they had been kept signed in until the session ended.
   */
  const [refreshError, setRefreshError] = useState<string | undefined>(undefined);
  const [protecting, setProtecting] = useState(false);

  if (serviceDegraded) {
    return (
      <div className="session-banner session-banner--degraded" role="status">
        <span>Mail service is temporarily unavailable — retrying. You are still signed in.</span>
      </div>
    );
  }

  if (binding.bound === false) {
    return (
      <div className="session-banner session-banner--unbound" role="status">
        <span>
          {binding.supported
            ? 'This session is not tied to this device, so a stolen cookie would work on its own. You cannot stay signed in on it.'
            : 'This browser can’t protect your session from cookie theft. You’ll be signed out after a short idle period and can’t stay signed in.'}
        </span>
        <span className="session-banner__spacer" />
        {binding.supported ? (
          // Opens the password prompt right here. It used to be a link to
          // Settings → Sessions, where the same words were a second button
          // that finally asked for the password: two presses of "Protect this
          // session" for one protection.
          <button
            type="button"
            className="btn btn--ghost"
            aria-haspopup="dialog"
            onClick={() => setProtecting(true)}
          >
            Protect this session
          </button>
        ) : null}
        {protecting && <ProtectSessionDialog onClose={() => setProtecting(false)} />}
      </div>
    );
  }

  if (!expiresSoon) return null;

  // The provider samples the clock on its tick; reading `Date.now()` here would
  // make render impure and the number would still only change on a re-render.
  const minutes = expiresInMs === undefined ? 0 : Math.max(0, Math.ceil(expiresInMs / 60_000));

  return (
    <div className="session-banner session-banner--expiring" role="status">
      <span>
        Your session expires in {minutes} minute{minutes === 1 ? '' : 's'}.
      </span>
      {refreshError !== undefined && (
        <span className="session-banner__error" role="alert">
          Could not keep you signed in: {refreshError}
        </span>
      )}
      <span className="session-banner__spacer" />
      <button
        type="button"
        className="btn btn--ghost"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setRefreshError(undefined);
          refresh()
            .catch((cause: unknown) => setRefreshError(messageOf(cause, 'the server did not answer.')))
            .finally(() => setBusy(false));
        }}
      >
        {refreshError === undefined ? 'Stay signed in' : 'Try again'}
      </button>
    </div>
  );
}

/**
 * "Protect this session", asked where it was offered: the password, then
 * `POST /auth/rebind` through the session's own `rebindDevice` -- the same
 * call Settings → Sessions makes. Success removes the banner by itself (the
 * session now reports `bound`), which is also what closes this dialog.
 */
function ProtectSessionDialog({ onClose }: { onClose: () => void }) {
  const { account, rebindDevice } = useSession();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const passwordRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const passwordId = useId();
  useBackToClose(true, onClose);

  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  // Escape closes, bound on the node: a key handler in JSX on a `dialog`
  // role is what jsx-a11y (rightly) flags, as in the calendar's Dialog.
  const busyRef = useRef(busy);
  const closeRef = useRef(onClose);
  useEffect(() => {
    busyRef.current = busy;
    closeRef.current = onClose;
  });
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || busyRef.current) return;
      event.stopPropagation();
      closeRef.current();
    };
    form.addEventListener('keydown', onKeyDown);
    return () => form.removeEventListener('keydown', onKeyDown);
  }, []);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!password) return;
    setBusy(true);
    setError(undefined);
    try {
      await rebindDevice(password);
      setPassword('');
      onClose();
    } catch (cause) {
      setError(messageOf(cause, 'This session could not be protected.'));
      setBusy(false);
    }
  }

  return (
    <div className="reauth__scrim">
      <form
        ref={formRef}
        className="reauth__dialog card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onSubmit={(event) => void onSubmit(event)}
      >
        <h2 className="reauth__title" id={titleId}>
          Protect this session
        </h2>
        <p className="reauth__body" id={descriptionId}>
          Confirm your password to tie this session to this device. A stolen cookie will no longer
          work on its own, and you will be able to stay signed in.
        </p>
        {error !== undefined && (
          <p className="reauth__error" role="alert">
            {error}
          </p>
        )}
        {account !== undefined && (
          <label className="reauth__field">
            <span>Email address</span>
            <input className="input" type="email" autoComplete="username" value={account.email} readOnly />
          </label>
        )}
        <div className="reauth__field">
          <label htmlFor={passwordId}>Password</label>
          <PasswordInput
            ref={passwordRef}
            id={passwordId}
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={busy}
          />
        </div>
        <div className="reauth__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy || !password}>
            {busy ? 'Protecting…' : 'Protect this session'}
          </button>
        </div>
      </form>
    </div>
  );
}
